import api from '@/api'
import { mergeMediaAssetRow } from '@/utils/mediaAssetRows'
import thumbnailCache from '@/services/thumbnailCache'
import vuex from '@/store'
import { utils } from '@/utils'

// Helper function to generate unique upload IDs
function generateUploadId() {
  return `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// Helper function to parse filename for automatic settings
function parseFilename(filename) {
  const result = {
    archive: false,
    tagObjects: []
  }

  const filenameLower = filename.toLowerCase()

  // PKG rule: if filename contains "PKG" (case insensitive), check archive and add PKG tag
  if (filenameLower.includes('pkg')) {
    result.archive = true
    // PKG tag is typically available
    const pkgTag = {
      value: 'PKG',
      type: 'video',
      settings: { type: 'Format' }
    }
    result.tagObjects.push(pkgTag)
  }

  return result
}

// Whether a pushed row carries a thumbnail override another user set or reset.
// A replaced override keeps the flag true, so updated_at tells it apart.
function thumbnailOverrideChanged(held, incoming) {
  if (!!incoming.has_thumbnail_override !== !!held.has_thumbnail_override) return true
  return !!incoming.has_thumbnail_override && incoming.updated_at !== held.updated_at
}

export default {
  state: {
    assets: [],
    currentAsset: {},
    lastUpdatedAt: 0,
    // Whether state.assets needs a reload, kept apart from the watermark: the
    // server-side Media Assets page advances the watermark without loading the
    // list, so a moved watermark cannot say the list is current (ND-2576).
    assetsStale: true,
    // The last broadcast applied, for components that own rows the store does
    // not — the server-side grid fetches its own blocks, so watching
    // state.assets would tell it nothing.
    lastBroadcast: null,
    uploads: [] // Single array with status: 'pending' | 'uploading' | 'completed' | 'error'
  },
  mutations: {
    LOAD_ASSETS(state, assets) {
      state.assets = assets
      console.debug(`LOAD_ASSETS`, assets)
    },
    LOAD_ASSET(state, asset) {
      const index = state.assets.findIndex((s) => s.id == asset.id)
      if (index > -1) state.assets.splice(index, 1, asset)
      else state.assets.push(asset)
      console.debug(`LOAD_ASSET`, asset)
    },
    SET_ASSET(state, object) {
      state.currentAsset = object
      console.debug(`SET_ASSET`, object?.name, object?.story_id)
    },
    SET_MEDIA_LAST_UPDATED_AT(state, updated_at_ts) {
      state.lastUpdatedAt = updated_at_ts
    },
    SET_ASSETS_STALE(state, stale) {
      state.assetsStale = stale
    },
    SET_LAST_MEDIA_BROADCAST(state, event) {
      state.lastBroadcast = event
    },
    UNLOAD_ASSET(state, assetId) {
      const index = state.assets.findIndex((a) => a.id == assetId)
      if (index > -1) state.assets.splice(index, 1)
      console.debug(`UNLOAD_ASSET`, assetId)
    },
    // Add new upload to the uploads array
    ADD_UPLOAD(state, upload) {
      if (!upload?.uploadId) throw new Error(`No uploadId provided`)
      // Check if upload already exists
      if (state.uploads.some((u) => u.uploadId === upload.uploadId)) {
        throw new Error(`Upload with id '${upload.uploadId}' already exists`)
      }
      state.uploads.push(upload)
    },
    // Update an upload by uploadId
    UPDATE_UPLOAD(state, { uploadId, changes }) {
      const upload = state.uploads.find((u) => u.uploadId === uploadId)
      if (upload) {
        Object.assign(upload, changes)
      }
    },
    // Remove an upload by uploadId
    REMOVE_UPLOAD(state, uploadId) {
      const index = state.uploads.findIndex((u) => u.uploadId === uploadId)
      if (index > -1) {
        state.uploads.splice(index, 1)
      }
    },
    // Replace all uploads
    SET_UPLOADS(state, uploads) {
      state.uploads = uploads
    }
  },
  actions: {
    // Get validation error for a media item
    getValidationError(context, { media }) {
      // Check if filename is empty or whitespace
      const filename = media.name || media.file?.name
      if (!filename || filename.trim() === '') {
        return 'Filename is required'
      }

      // Check for invalid characters in filename
      if (utils.hasInvalidFileNameChars(filename)) {
        return 'Filename contains invalid characters'
      }

      // Check if at least one format type tag is selected
      if (!media.selectedTags || media.selectedTags.length === 0) {
        return 'Missing format tag'
      }

      // Check if at least one format type tag is selected
      const hasFormatTag = media.selectedTags.some((tag) => {
        const tagType = tag?.settings?.type
        return tagType && tagType.toLowerCase() === 'format'
      })

      if (!hasFormatTag) {
        return 'Missing format tag'
      }

      return null
    },

    // Check if media is ready to upload
    isReadyToUpload({ dispatch }, { media, availableTags }) {
      return dispatch('getValidationError', { media, availableTags }) === null
    },

    // Add a new file to pending uploads
    // `transcribe` requests a transcription once the proxy is built (ND-2547):
    // on for video headed for publishing (a script, an article's lead).
    addFile({ commit }, { file, defaultDestinations, storyId = null, tags, archive, name, transcribe = false }) {
      const originalName = name ?? file.name

      // Reject files with invalid filename characters
      if (utils.hasInvalidFileNameChars(originalName)) {
        return { success: false, error: 'Filename contains invalid characters', filename: originalName }
      }

      // Parse filename to get automatic settings
      const parsedSettings = parseFilename(file.name)

      // Create upload item with status 'pending'
      const uploadId = generateUploadId()
      const upload = {
        uploadId,
        file,
        name: originalName,
        status: 'pending', // 'pending' | 'uploading' | 'completed' | 'error'
        progress: null,
        archive: archive || parsedSettings.archive,
        transcribe: !!transcribe,
        selectedDestinations: defaultDestinations,
        error: null,
        cancelTokenSource: null,
        newAsset: null,
        selectedTags: tags ?? parsedSettings.tagObjects ?? [],
        showFormatSelector: false,
        showRestrictionsSelector: false,
        storyId: storyId
      }

      // Add to store
      commit('ADD_UPLOAD', upload)
      return { success: true, uploadId }
    },

    // Clear all pending uploads
    clearPendingUploads({ state, commit }) {
      const uploadsToKeep = state.uploads.filter((u) => u.status !== 'pending')
      commit('SET_UPLOADS', uploadsToKeep)
    },

    // Remove an upload by uploadId
    removeUpload({ commit }, uploadId) {
      commit('REMOVE_UPLOAD', uploadId)
    },

    // Update an upload
    updateUpload({ commit }, { uploadId, changes }) {
      commit('UPDATE_UPLOAD', { uploadId, changes })
    },

    // Set all uploads
    setUploads({ commit }, uploads) {
      commit('SET_UPLOADS', uploads)
    },

    // Toggle archive for all pending items
    toggleAllArchive({ state, commit }) {
      const pendingUploads = state.uploads.filter((u) => u.status === 'pending')
      if (!pendingUploads?.length) return false

      const allArchiveSelected = pendingUploads.length > 0 && pendingUploads.every((u) => u.archive === true)
      const shouldSelectAll = !allArchiveSelected

      const updated = state.uploads.map((upload) => {
        if (upload.status === 'pending') {
          return { ...upload, archive: shouldSelectAll }
        }
        return upload
      })

      commit('SET_UPLOADS', updated)
      return shouldSelectAll
    },

    // Toggle transcribe for all pending items
    toggleAllTranscribe({ state, commit }) {
      const pendingUploads = state.uploads.filter((u) => u.status === 'pending')
      if (!pendingUploads?.length) return false

      const allTranscribeSelected = pendingUploads.length > 0 && pendingUploads.every((u) => u.transcribe === true)
      const shouldSelectAll = !allTranscribeSelected

      const updated = state.uploads.map((upload) => {
        if (upload.status === 'pending') {
          return { ...upload, transcribe: shouldSelectAll }
        }
        return upload
      })

      commit('SET_UPLOADS', updated)
      return shouldSelectAll
    },

    /**
     * Apply one media asset broadcast to the list we hold.
     *
     * Nothing here fetches: the point is to replace a 400-row reload with a merge
     * of the row the event already carries. The row arrives without the
     * transcript keys of meta_data, and nothing on a list reads them; the asset
     * modal loads the asset itself when it opens.
     */
    applyMediaBroadcast({ commit, state, dispatch }, event) {
      const assetId = event?.media_asset_id
      if (!assetId) return

      dispatch('advanceMediaWatermark', event)

      const isOpen = state.currentAsset?.id == assetId

      // Announced before the branches below so a component sees every event,
      // including the deletes that return early.
      commit('SET_LAST_MEDIA_BROADCAST', event)

      if (event.action === 'deleted') {
        commit('UNLOAD_ASSET', assetId)
        // Whoever has it open is looking at something that no longer exists and
        // every later fetch will agree, so clear it rather than leave a ghost.
        if (isOpen) commit('SET_ASSET', {})
        return
      }

      if (!event.media_asset) return

      const existing = state.assets.find((a) => a.id == assetId)

      // LOAD_ASSET splices rather than mutating in place, which is what makes the
      // grid's row comparison see a change.
      if (existing) {
        // Only the tab that changed the thumbnail drops its cached URL; every
        // other tab has to do it here or keep showing the old image (ND-2415).
        if (thumbnailOverrideChanged(existing, event.media_asset)) thumbnailCache.invalidate(assetId)
        commit('LOAD_ASSET', mergeMediaAssetRow(existing, event.media_asset))
      } else if (event.action === 'created') {
        // Not held. Only a create belongs in the list; an update for a row we
        // do not have is an asset outside the loaded scope, and inserting it
        // would put a row on screen the fetch deliberately left out.
        commit('LOAD_ASSET', event.media_asset)
      }

      if (isOpen) commit('SET_ASSET', mergeMediaAssetRow(state.currentAsset, event.media_asset))
    },

    /**
     * Keep the /changes watermark level with what push has already delivered, so
     * the poll only fires on changes this tab never received. That includes the
     * tab's own writes, whose echo is otherwise ignored: the writer already holds
     * the result, and a watermark left behind would cost it the 400-row reload.
     *
     * floor(), not ceil(): erring low costs a redundant refetch, erring high would
     * skip a real one.
     */
    advanceMediaWatermark({ commit, state }, event) {
      const appliedAt = Math.floor(Date.parse(event?.timestamp) / 1000)
      if (Number.isFinite(appliedAt) && appliedAt > state.lastUpdatedAt) {
        commit('SET_MEDIA_LAST_UPDATED_AT', appliedAt)
      }
    },

    /**
     * Whether media changed since the last check, advancing the watermark.
     *
     * Split out of getMediaChanges so a caller that owns its own rows — the
     * server-side asset grid — can react to a change without pulling the whole
     * list into the store first.
     */
    async checkMediaChanges({ commit, state }) {
      let response = await api.newsdesk.getChanges()
      if (!response) return false

      let mediaAssetsChange = response.data?.find((c) => c.change_type == 'media')
      if (!(state.lastUpdatedAt < mediaAssetsChange?.updated_at_ts)) return false

      // The first check only sets the baseline. After that, a watermark that moved
      // means a change push never delivered. The layout's poll, the Media Assets
      // page's poll and the reconnect resync all advance this one watermark, so
      // whichever finds the change announces it for the others; the server-side
      // grid owns its rows and refreshes on the announcement.
      const missedPush = state.lastUpdatedAt > 0
      commit('SET_MEDIA_LAST_UPDATED_AT', mediaAssetsChange?.updated_at_ts)
      // Whoever found it, the list in the store has not seen it yet.
      commit('SET_ASSETS_STALE', true)
      if (missedPush) commit('SET_LAST_MEDIA_BROADCAST', { action: 'resync' })
      return true
    },
    async getMediaChanges({ dispatch, state }) {
      await dispatch('checkMediaChanges')
      if (state.assetsStale) await dispatch('loadAssets', { skipCache: true })
    },
    async loadAssets({ commit, state, dispatch }, { params = null, skipCache = true }) {
      // Cleared before the fetch, so a change found while it is in flight marks
      // the list stale again and the next check reloads it.
      commit('SET_ASSETS_STALE', false)
      let _s
      try {
        _s = await api.newsdesk.getMediaAssets(params, skipCache)
      } catch (error) {
        commit('SET_ASSETS_STALE', true)
        throw error
      }
      const assets = _s?.data?.results ?? []

      commit('LOAD_ASSETS', assets)

      // We should update the current story object also.
      if (state?.currentAsset?.id) {
        let match = assets.find((asset) => asset.id == state.currentAsset.id)

        if (match) {
          dispatch('setAsset', match)
        }
      }
    },

    async loadAsset({ commit, dispatch }, { skipCache, id }) {
      const _s = await api.newsdesk.getMediaAsset(id, skipCache)
      const asset = _s.data // ?.data?.find(s => s.id == id)
      commit('LOAD_ASSET', asset)
      dispatch('setAsset', asset)
      return asset
    },

    async setAsset({ commit }, asset) {
      commit('SET_ASSET', asset)
    },
    async saveAsset({ commit }, { id, data }) {
      console.debug('SAVING ASSET', id, data)
      const _s = await api.newsdesk.updateMediaAsset(id, data)
      let updatedAsset = _s?.data
      if (updatedAsset) {
        commit('LOAD_ASSET', updatedAsset)
        // The API leaves this tab out of the broadcast of its own write, and the
        // server-side grid does not read state.assets, so announce the saved row
        // the way a broadcast would (ND-2454). `local` marks it as this tab's own.
        commit('SET_LAST_MEDIA_BROADCAST', { action: 'updated', media_asset_id: updatedAsset.id, media_asset: updatedAsset, local: true })
      }
      return updatedAsset
    },
    async cloneAsset({ commit }, { id, data }) {
      const _s = await api.newsdesk.cloneMediaAsset(id, data)
      let clonedAsset = _s?.data
      if (clonedAsset?.id) {
        commit('LOAD_ASSET', clonedAsset)
        // The echo of this create carries this tab's socket id and is dropped, and
        // the watermark it advances can hide the change from the /changes poll, so
        // without this the rundown Video ID never sees the clone (ND-2571).
        commit('SET_LAST_MEDIA_BROADCAST', { action: 'created', media_asset_id: clonedAsset.id, media_asset: clonedAsset, local: true })
      }
      return clonedAsset
    },
    // Start uploading an upload (change status from pending to uploading)
    async startUploadProcess({ commit, state }, uploadId) {
      const upload = state.uploads.find((u) => u.uploadId === uploadId)
      if (!upload) return

      // Update status to uploading and set up cancel token
      const cancelTokenSource = api.newsdesk.getCancelTokenSource()
      commit('UPDATE_UPLOAD', {
        uploadId,
        changes: { status: 'uploading', cancelTokenSource, startTime: Date.now() }
      })

      let data = {
        name: upload.name,
        file: upload.file,
        story_id: upload.storyId,
        archive: upload.archive ? 1 : 0,
        transcribe_after_proxy: upload.transcribe ? 1 : 0,
        station: upload.station ?? vuex.getters.currentStation.station,
        destinations: upload.selectedDestinations,
        tags: upload.selectedTags
      }

      try {
        let { mediaAsset } = await api.newsdesk.addMediaAsset(
          data,
          (progress) => {
            // update the upload progress
            commit('UPDATE_UPLOAD', {
              uploadId,
              changes: { progress }
            })
          },
          cancelTokenSource.token,
          (newAsset) => {
            commit('UPDATE_UPLOAD', {
              uploadId,
              changes: { newAsset }
            })
          }
        )

        // Mark as completed
        commit('UPDATE_UPLOAD', {
          uploadId,
          changes: { status: 'completed' }
        })

        // add new asset to vuex store
        commit('LOAD_ASSET', mediaAsset)
        // The API broadcasts the create from this tab's own request, so the echo
        // carries this socket id and is dropped; announce the new row the way
        // saveAsset announces its own write (ND-2454).
        commit('SET_LAST_MEDIA_BROADCAST', { action: 'created', media_asset_id: mediaAsset.id, media_asset: mediaAsset, local: true })

        // if currentStory id is same as new asset story id, update current asset (via dispatch loadStory)
        let currentStory = vuex.getters.currentStory
        if (currentStory?.id && currentStory.id == mediaAsset.story_id) {
          console.log('Reloading current story to include new media asset', currentStory.id, mediaAsset.id)
          await vuex.dispatch('loadStory', { id: currentStory.id, skipCache: true, setStory: true })
        }

        // Remove after a delay
        setTimeout(() => {
          commit('REMOVE_UPLOAD', uploadId)
        }, 100)
      } catch (error) {
        // only set error if it has a message. Cancelled uploads will not have a message
        if (error.message) {
          commit('UPDATE_UPLOAD', {
            uploadId,
            changes: { status: 'error', error }
          })
          console.error('Failed to upload', upload, error)

          // Send client-only notification to user
          // Extract validation messages from Laravel 422 responses
          const responseErrors = error.response?.data?.errors
          const responseMessage = error.response?.data?.message
          let errorMessage
          if (responseErrors) {
            errorMessage = Object.values(responseErrors).flat().join(', ')
          } else {
            errorMessage = responseMessage || error.message || 'Unknown error'
          }
          const notificationMessage = `Failed to upload "${upload.name}": ${errorMessage}`
          vuex.dispatch('addNotification', {
            message: notificationMessage,
            level: 'error',
            data: { type: 'upload_failure', uploadId, uploadName: upload.name }
          })
        } else {
          console.warn('Upload cancelled', upload)
          commit('REMOVE_UPLOAD', uploadId)
        }
      }
    },

    // tell the server to abort the multi part upload so the server can clean up
    abortActiveUpload({ state }, uploadId) {
      const upload = state.uploads.find((u) => u.uploadId === uploadId)
      if (!upload || !upload.newAsset?.id) return
      return api.newsdesk.abortMultiPartUpload(upload.newAsset.id, upload.file.name)
    },

    async cancelActiveUpload({ state, dispatch, commit }, uploadId) {
      const upload = state.uploads.find((u) => u.uploadId === uploadId)
      if (!upload) return

      // Cancel the axios request if it's in progress
      upload.cancelTokenSource?.cancel()

      // Abort the multi part upload so the server can clean up
      try {
        await dispatch('abortActiveUpload', uploadId)
      } catch (error) {
        console.error('Failed to abort multi part upload', error)
      }

      // Remove the upload
      commit('REMOVE_UPLOAD', uploadId)
    },

    // Start upload for a single media item
    async upload({ dispatch }, { uploadId }) {
      try {
        // Start the upload process
        await dispatch('startUploadProcess', uploadId)
      } catch (error) {
        console.error('Error starting upload', uploadId, error)
        throw error
      }
    },

    // Upload all ready items
    async uploadAll({ dispatch, state, rootGetters }) {
      // Get availableTags from root store
      const publishedVideoTags = rootGetters.publishedVideoTags ?? []
      const availableTags = publishedVideoTags.filter((tag) => tag?.value && tag.value.length > 0)

      // Get all pending uploads
      const itemsToProcess = state.uploads.filter((item) => item.status === 'pending')

      // Validate and collect valid uploadIds
      const validUploadIds = []
      for (const item of itemsToProcess) {
        const error = await dispatch('getValidationError', { media: item, availableTags })
        if (!error) {
          validUploadIds.push(item.uploadId)
        }
      }

      // Start all uploads asynchronously (don't wait for each to complete)
      const uploadPromises = validUploadIds.map((uploadId) =>
        dispatch('upload', { uploadId }).catch((error) => {
          console.error('Failed to upload item with id', uploadId, error)
        })
      )

      // Wait for all uploads to complete
      await Promise.allSettled(uploadPromises)
    },

    // Cancel all uploads
    async cancelAllUploads({ dispatch, state }) {
      // Get all pending and uploading uploads
      const uploadsToCancel = state.uploads.filter((u) => u.status === 'pending' || u.status === 'uploading')

      // Cancel each upload
      const cancelPromises = uploadsToCancel.map((upload) => dispatch('cancelActiveUpload', upload.uploadId))
      await Promise.allSettled(cancelPromises)
    },

    // Handle tag selector toggle
    handleTagSelectorToggle({ state, commit }, uploadId) {
      // Close all other selectors first
      const updated = state.uploads.map((item) => {
        if (item.uploadId === uploadId) {
          // Toggle this one
          return { ...item, showFormatSelector: !item.showFormatSelector, showRestrictionsSelector: false }
        }
        // Close others
        return { ...item, showFormatSelector: false, showRestrictionsSelector: false }
      })
      commit('SET_UPLOADS', updated)
    },

    // Handle archive checkbox change
    handleArchiveChange({ state, commit }, { uploadId, isArchived }) {
      const upload = state.uploads.find((u) => u.uploadId === uploadId)
      if (!upload) return

      // If unchecking archive, clear destinations
      const changes = { archive: isArchived }
      if (!isArchived) {
        changes.selectedDestinations = []
      }

      commit('UPDATE_UPLOAD', { uploadId, changes })
    },

    // Apply tags from one upload to all other pending uploads
    applyTagsToAllPending({ state, commit }, { sourceUploadId, tags }) {
      if (!tags || tags.length === 0) return

      // Update all pending uploads except the source
      const updated = state.uploads.map((upload) => {
        if (upload.status === 'pending' && upload.uploadId !== sourceUploadId) {
          // Merge tags, avoiding duplicates
          const existingTags = upload.selectedTags ?? []
          const mergedTags = [...existingTags]

          tags.forEach((newTag) => {
            if (!mergedTags.some((t) => t.value === newTag.value)) {
              mergedTags.push(newTag)
            }
          })

          return { ...upload, selectedTags: mergedTags }
        }
        return upload
      })

      commit('SET_UPLOADS', updated)
    }
  },
  getters: {
    assets(state) {
      return state.assets
    },
    currentAsset(state) {
      return state.currentAsset ?? {}
    },
    activeUploads(state) {
      return state.uploads.filter((u) => u.status === 'uploading')
    },
    newMedia(state) {
      return state.uploads.filter((u) => u.status === 'pending')
    }
  }
}
